import axios, {
  type AxiosInstance,
  type AxiosError,
  type AxiosRequestHeaders,
  AxiosHeaders
} from 'axios'
import type { ApiError } from '@/types/api.types'
import { loggerService } from './logger.service'
import { planLimitState } from '@/composables/usePlanLimit'
import { normalizePlanLimitType } from '@/utils/planLimit'
import { FEATURE_NOT_ENABLED_CODE } from '@/utils/featureErrors'
import { refreshSessionCookies } from './auth-refresh'
import { performLogout } from '@/utils/logout-cleanup'
import { isPublicAuthRoute } from '@/utils/public-auth-routes'
import { rewriteRequestApiPath } from '@/utils/apiBase'
import { sessionExpiredState } from '@/composables/useSessionExpired'
import { tenantDataState } from '@/composables/useTenantDataRevision'
import { isMutatingRequest } from '@/utils/mutatingRequest'

declare module 'axios' {
  export interface AxiosRequestConfig {
    skipAuthRefresh?: boolean
    _retry?: boolean
    /** Don't log this request's own failures — set on requests made by loggerService itself. */
    skipErrorLogging?: boolean
    /** Refresh on a 401 even from a guest page — set on requests a signed-in user initiates there. */
    forceAuthRefresh?: boolean
  }
}

const api: AxiosInstance = axios.create({
  baseURL: (import.meta.env.VITE_API_BASE_URL || '/api').replace(/\/api\/?$/, ''),
  timeout: 60000,
  withCredentials: true,
  xsrfCookieName: 'csrf_access_token',
  xsrfHeaderName: 'X-CSRF-TOKEN',
  headers: {
    'Content-Type': 'application/json'
  }
})

function extractApiErrorFields(
  data: unknown,
  fallback: string,
): {
  message: string
  code?: string
  errors?: Record<string, string[]>
  missing?: unknown[]
} {
  if (data && typeof data === 'object') {
    const body = data as Record<string, unknown>
    let message = fallback
    if (typeof body.message === 'string' && body.message.trim()) {
      message = body.message
    } else if (typeof body.detail === 'string' && body.detail.trim()) {
      message = body.detail
    }
    const code = typeof body.code === 'string' ? body.code : undefined
    const errors =
      body.errors && typeof body.errors === 'object' && !Array.isArray(body.errors)
        ? (body.errors as Record<string, string[]>)
        : undefined
    const missing = Array.isArray(body.missing) ? body.missing : undefined
    return { message, code, errors, missing }
  }
  return { message: fallback }
}

function redirectToLogin(): void {
  window.location.href = new URL('/auth/login', window.location.origin).href
}

/** "METHOD /path" for a failing request, so server-error reports say which endpoint 500'd. */
function describeFailedRequest(config?: { method?: string; url?: string }): string | undefined {
  if (!config?.url) {
    return undefined
  }
  const method = config.method ? config.method.toUpperCase() : ''
  return `${method} ${config.url}`.trim()
}

/** Axios omits `false` when applying headers; lets the runtime set multipart boundary for FormData. */
function stripJsonContentTypeForFormData(config: { headers?: AxiosRequestHeaders }): void {
  const h = AxiosHeaders.from(config.headers)
  h.set('Content-Type', false)
  config.headers = h
}

api.interceptors.request.use(
  (config) => {
    if (config.url) {
      config.url = rewriteRequestApiPath(config.url)
    }
    if (typeof FormData !== 'undefined' && config.data instanceof FormData) {
      stripJsonContentTypeForFormData(config)
    }
    return config
  },
  (error) => {
    loggerService.logError(
      error instanceof Error ? error : new Error('Request failed'),
      { action: 'api_request' }
    )
    return Promise.reject(error)
  }
)

api.interceptors.response.use(
  (response) => {
    if (isMutatingRequest(response.config.method)) {
      tenantDataState.markChanged()
    }
    return response
  },
  async (error: AxiosError<ApiError>) => {
    const status = error.response?.status
    const originalConfig = error.config

    if (!error.response) {
      const networkError = new Error(
        error.code === 'ECONNABORTED'
          ? 'Request timeout. Please check your connection.'
          : 'Network error. Please check your internet connection.'
      )
      if (!originalConfig?.skipErrorLogging) {
        loggerService.logError(networkError, { action: 'api_network_error' })
      }
      return Promise.reject({
        message: networkError.message,
        status: 0,
        errors: {}
      } as ApiError)
    }

    switch (status) {
      case 401: {
        if (!originalConfig?.forceAuthRefresh && isPublicAuthRoute(window.location.pathname)) {
          break
        }

        if (
          originalConfig &&
          !originalConfig.skipAuthRefresh &&
          !originalConfig._retry
        ) {
          originalConfig._retry = true
          try {
            await refreshSessionCookies()
            return api(originalConfig)
          } catch {
            const choice = await sessionExpiredState.show(true)
            if (choice === 'stay') {
              try {
                await refreshSessionCookies()
                return api(originalConfig)
              } catch {
                await performLogout()
                redirectToLogin()
                return Promise.reject({
                  message: 'Session expired',
                  status: 401,
                  errors: {},
                  code: 'unauthorized',
                } as ApiError)
              }
            }
            await performLogout()
            redirectToLogin()
            return Promise.reject({
              message: 'Session expired',
              status: 401,
              errors: {},
              code: 'unauthorized',
            } as ApiError)
          }
        }
        break
      }

      case 402: {
        const raw = error.response?.data
        const data =
          raw && typeof raw === 'object'
            ? (raw as unknown as Record<string, unknown>)
            : undefined
        if (data?.feature === 'plan_limit_exceeded') {
          const limit = normalizePlanLimitType(data.limit)
          const used = typeof data.used === 'number' ? data.used : Number(data.used ?? 0)
          const max = typeof data.max === 'number' ? data.max : Number(data.max ?? 0)
          loggerService.logWarning('Plan limit exceeded', {
            action: 'api_plan_limit',
            planLimit: limit,
            planUsed: used,
            planMax: max,
          })
          planLimitState.show({ limit, used, max })
          return Promise.reject({
            message:
              typeof data.message === 'string'
                ? data.message
                : 'Plan limit exceeded',
            status: 402,
            errors: error.response?.data?.errors,
            feature: 'plan_limit_exceeded',
            limit,
            used,
            max,
            planLimitExceeded: true,
          } as ApiError)
        }
        if (data?.code === FEATURE_NOT_ENABLED_CODE) {
          // Expected denial, not a failure: carry the feature key so callers can
          // render the upsell panel, and skip the generic >=500 error logging.
          return Promise.reject({
            message:
              typeof data.message === 'string'
                ? data.message
                : 'This feature is not enabled for this workspace',
            status: 402,
            errors: error.response?.data?.errors,
            code: FEATURE_NOT_ENABLED_CODE,
            feature: typeof data.feature === 'string' ? data.feature : undefined,
          } as ApiError)
        }
        break
      }

      case 403: {
        const data = error.response?.data as unknown as Record<string, unknown> | undefined
        const detailRaw = data?.detail
        const detail =
          typeof detailRaw === 'string' ? detailRaw : detailRaw != null ? String(detailRaw) : ''
        const msg = typeof data?.message === 'string' ? data.message : ''
        const staleHdr = error.response?.headers['x-tenant-session-stale']
        if (
          !isPublicAuthRoute(window.location.pathname) &&
          (detail === 'tenant_mismatch' ||
            msg === 'tenant_mismatch' ||
            staleHdr === '1')
        ) {
          await performLogout()
          redirectToLogin()
          return Promise.reject({
            message: 'Workspace mismatch. Please sign in again.',
            status: 403,
            errors: {}
          } as ApiError)
        }
        if (
          !isPublicAuthRoute(window.location.pathname) &&
          (detail === 'tenant_suspended' || msg === 'tenant_suspended')
        ) {
          await performLogout()
          window.location.href = new URL(
            '/auth/login?reason=suspended',
            window.location.origin,
          ).href
          return Promise.reject({
            message: 'This workspace has been suspended.',
            status: 403,
            errors: {}
          } as ApiError)
        }
        loggerService.logWarning('Access forbidden', { action: 'api_forbidden' })
        break
      }

      case 404:
        loggerService.logWarning('Resource not found', { action: 'api_not_found' })
        break

      case 422:
        break

      case 429:
        loggerService.logWarning('Rate limit exceeded', { action: 'api_rate_limit' })
        break

      case 500:
      case 502:
      case 503:
      case 504: {
        const serverError = new Error(
          error.response?.data?.message || 'Server error. Please try again later.'
        )
        loggerService.logError(serverError, {
          action: 'api_server_error',
          route: window.location.pathname,
          endpoint: describeFailedRequest(originalConfig)
        })
        break
      }

      default:
        if (status && status >= 500) {
          const unknownError = new Error(
            error.response?.data?.message || 'An unexpected error occurred.'
          )
          loggerService.logError(unknownError, {
            action: 'api_unknown_error',
            route: window.location.pathname,
            endpoint: describeFailedRequest(originalConfig)
          })
        }
    }

    const fallbackMessage =
      status === 422
        ? 'validation_error'
        : status === 500
          ? 'Server error'
          : 'An error occurred'

    const { message: errorMessage, code, errors, missing } = extractApiErrorFields(
      error.response?.data,
      error.message || fallbackMessage,
    )

    const resolvedCode =
      code ?? (status === 422 ? 'validation_error' : undefined)

    // Reject with the original AxiosError so the ~15 `err.response?.status` /
    // `err.response?.data` branches at the call sites stay alive, while the
    // parsed fields remain attached for `err.status` / `err.code` / `err.errors`
    // consumers (GS-D1).
    return Promise.reject(
      Object.assign(error, {
        message: errorMessage,
        status: status || 500,
        code: resolvedCode,
        errors: errors ?? error.response?.data?.errors,
        missing: missing ?? error.response?.data?.missing,
      })
    )
  }
)

export default api
